import { z } from 'zod/v4';
import { PerformedBySchema } from '../performed-by/index.js';
import { ViewDescriptionSchema } from '../views/index.js';
import { ViewVisibilityType } from '../views/visibility.js';
import { BillingReasonType } from '../economy/billing-models/index.js';
import { Currency, Entity } from '../index.js';
import { BlameLogRefType, BlameLogType } from '../blamelog/index.js';
import { CaseVoucherSource, PaymentType, State, } from '../economy/case-vouchers/index.js';
import { CaseStatus, CaseType } from '../cases/index.js';
import { CollectorType } from '../collectors/index.js';
import { ConnectCaseMappingStatus, EntityType, } from '../connect/mappings/index.js';
import { ConnectConnectionStatus } from '../connect/connections/index.js';
import { EventType, SourceType } from '../cosmo/events/index.js';
import { CreditorState } from '../creditor/index.js';
import { DocumentTypes, RefIdTypes } from '../documents/index.js';
import { FileType } from '../files/index.js';
import { Direction, IncomingState, InteractionType, OutgoingState, } from '../interactions/index.js';
import { PaymentMethodStatus, Provider, } from '../payment-methods/index.js';
import { PaymentPlanEntryStatus, PaymentPlanEntryTypes, PaymentPlanStatus, } from '../economy/payment-plans/index.js';
import { PayoutState, PayoutType } from '../economy/payouts/index.js';
import { PropertyDataSchema, PropertyDataType, } from '../properties/index.js';
import { roles } from '../users/roles.js';
import { TemplateState, TemplateType } from '../templates/index.js';
import { TransactionAccountType } from '../transaction-accounts/index.js';
import { TribunalCaseStatus, TribunalCaseType, } from '../tribunal/cases/types.js';
import { TribunalJobType } from '../tribunal/jobs/types.js';
import { UpdateReferenceTypes, UpdateState } from '../updates/index.js';
import { VoucherCategory } from '../economy/vouchers/index.js';
import { UserOnCustomerDetailsSchema } from '../customers/index.js';
import { ImportState } from '../imports/types.js';
export const em = (e) => e;
export const schema = em({
    entities: {
        billingAppendices: {
            tableName: 'billing_appendices',
            primaryKey: 'billing_appendix_id',
            scope: 'billings',
            properties: {
                billingAppendixSetupId: {
                    columnName: 'billing_appendix_setup_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                billingAppendixId: {
                    columnName: 'billing_appendix_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                includeVatInDepositShare: {
                    columnName: 'include_vat_in_deposit_share',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                timingType: {
                    columnName: 'timing_type',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                hideFromCreditor: {
                    columnName: 'hide_from_creditor',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                timingDetails: {
                    columnName: 'timing_details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                onlyVoucherTypeIds: {
                    columnName: 'only_voucher_type_ids',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
            },
        },
        billingAppendixSetups: {
            tableName: 'billing_appendix_setups',
            primaryKey: 'billing_appendix_setup_id',
            scope: 'billing-appendix-setups',
            properties: {
                archived: {
                    columnName: 'archived',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                billingAppendixSetupId: {
                    columnName: 'billing_appendix_setup_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
            },
        },
        billingAppendixSetupsOnTenants: {
            tableName: 'billing_appendix_setups_on_tenants',
            primaryKey: 'billing_appendix_setup_id',
            scope: 'billing-appendix-setups',
            properties: {
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                billingAppendixSetupId: {
                    columnName: 'billing_appendix_setup_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        billingCollections: {
            tableName: 'billing_collections',
            primaryKey: 'billing_collection_id',
            scope: 'billings',
            properties: {
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                billingCollectionId: {
                    columnName: 'billing_collection_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        billingModels: {
            tableName: 'billing_models',
            primaryKey: 'billing_model_id',
            scope: 'billing-models',
            properties: {
                billingModelId: {
                    columnName: 'billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                model: {
                    columnName: 'model',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                archived: {
                    columnName: 'archived',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
            },
        },
        billingModelsOnTenants: {
            tableName: 'billing_models_on_tenants',
            primaryKey: 'id',
            scope: 'billing-models',
            properties: {
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                billingModelId: {
                    columnName: 'billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        billingReasons: {
            tableName: 'billing_reasons',
            primaryKey: 'billing_reason_id',
            scope: 'billing-reasons',
            properties: {
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(BillingReasonType),
                    },
                },
                depositCaseVoucherIds: {
                    columnName: 'deposit_case_voucher_ids',
                    type: {
                        type: 'unknown',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                performedBy: {
                    columnName: 'performed_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                billingId: {
                    columnName: 'billing_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingReasonId: {
                    columnName: 'billing_reason_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        billingVouchers: {
            tableName: 'billing_vouchers',
            primaryKey: 'billing_voucher_id',
            scope: 'billing-vouchers',
            properties: {
                billingReasonId: {
                    columnName: 'billing_reason_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billAfter: {
                    columnName: 'bill_after',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingId: {
                    columnName: 'billing_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                shareId: {
                    columnName: 'share_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingModelId: {
                    columnName: 'billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingAppendixId: {
                    columnName: 'billing_appendix_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingVoucherId: {
                    columnName: 'billing_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                depositCaseVoucherId: {
                    columnName: 'deposit_case_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                isAdjustment: {
                    columnName: 'is_adjustment',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                deletedBy: {
                    columnName: 'deleted_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                deletedAt: {
                    columnName: 'deleted_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                amount: {
                    columnName: 'amount',
                    type: {
                        type: 'money',
                        nullable: false,
                    },
                },
                voucherTypeId: {
                    columnName: 'voucher_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                caseVoucherId: {
                    columnName: 'case_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                date: {
                    columnName: 'date',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                vatOfBillingVoucherId: {
                    columnName: 'vat_of_billing_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                depositCaseVoucher: {
                    columnName: 'deposit_case_voucher',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                text: {
                    columnName: 'text',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                currency: {
                    columnName: 'currency',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Currency),
                    },
                },
                caseVoucher: {
                    columnName: 'case_voucher',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                deletingBillingVoucherId: {
                    columnName: 'deleting_billing_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        billings: {
            tableName: 'billings',
            primaryKey: 'billing_id',
            scope: 'billings',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                pdfFileId: {
                    columnName: 'pdf_file_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                currency: {
                    columnName: 'currency',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Currency),
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: true,
                    },
                },
                billingId: {
                    columnName: 'billing_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                billingCollectionId: {
                    columnName: 'billing_collection_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                billingAppendixId: {
                    columnName: 'billing_appendix_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                isAdjustment: {
                    columnName: 'is_adjustment',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                billingEmailed: {
                    columnName: 'billing_emailed',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                addedToPayouts: {
                    columnName: 'added_to_payouts',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                date: {
                    columnName: 'date',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                xlsxFileId: {
                    columnName: 'xlsx_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        blameLogs: {
            tableName: 'blame_logs',
            primaryKey: 'blame_log_id',
            scope: 'blamelogs',
            properties: {
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                loggedAt: {
                    columnName: 'logged_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                performedBy: {
                    columnName: 'performed_by',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: PerformedBySchema,
                    },
                },
                refId: {
                    columnName: 'ref_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                blameLogId: {
                    columnName: 'blame_log_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(BlameLogType),
                    },
                },
                refType: {
                    columnName: 'ref_type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(BlameLogRefType),
                    },
                },
                ip: {
                    columnName: 'ip',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                userAgent: {
                    columnName: 'user_agent',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                referer: {
                    columnName: 'referer',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        caseGroups: {
            tableName: 'case_groups',
            primaryKey: 'case_group_id',
            scope: 'casegroups',
            properties: {
                settings: {
                    columnName: 'settings',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                caseGroupId: {
                    columnName: 'case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        caseVouchers: {
            tableName: 'case_vouchers',
            primaryKey: 'case_voucher_id',
            scope: 'case-vouchers',
            properties: {
                tags: {
                    columnName: 'tags',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                importId: {
                    columnName: 'import_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                referenceCaseVoucherId: {
                    columnName: 'reference_case_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                voucherTypeId: {
                    columnName: 'voucher_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                amount: {
                    columnName: 'amount',
                    type: {
                        type: 'money',
                        nullable: false,
                    },
                },
                dueDate: {
                    columnName: 'due_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                date: {
                    columnName: 'date',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                transactionAccountId: {
                    columnName: 'transaction_account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                caseVoucherId: {
                    columnName: 'case_voucher_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                paymentType: {
                    columnName: 'payment_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(PaymentType),
                    },
                },
                text: {
                    columnName: 'text',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                interest: {
                    columnName: 'interest',
                    type: {
                        type: 'float',
                        nullable: true,
                    },
                },
                currency: {
                    columnName: 'currency',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Currency),
                    },
                },
                source: {
                    columnName: 'source',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CaseVoucherSource),
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                interestStartDate: {
                    columnName: 'interest_start_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                interestEndDate: {
                    columnName: 'interest_end_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(State),
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                lineItems: {
                    columnName: 'line_items',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                paymentMethodId: {
                    columnName: 'payment_method_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                expired: {
                    columnName: 'expired',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                expirationManualSet: {
                    columnName: 'expiration_manual_set',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                expirationDate: {
                    columnName: 'expiration_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                irrecoverableDate: {
                    columnName: 'irrecoverable_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                deletedAt: {
                    columnName: 'deleted_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                deletedBy: {
                    columnName: 'deleted_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                exported: {
                    columnName: 'exported',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                depositOnPaymentPlanEntryId: {
                    columnName: 'deposit_on_payment_plan_entry_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                transactionImportId: {
                    columnName: 'transaction_import_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                expirationFromDate: {
                    columnName: 'expiration_from_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                cardType: {
                    columnName: 'card_type',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                uniqueId: {
                    columnName: 'unique_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                appendix: {
                    columnName: 'appendix',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
            },
        },
        cases: {
            tableName: 'cases',
            primaryKey: 'case_id',
            scope: 'cases',
            properties: {
                chainId: {
                    columnName: 'chain_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                responsibleType: {
                    columnName: 'responsible_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: ['user', 'team'],
                    },
                },
                currency: {
                    columnName: 'currency',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Currency),
                    },
                },
                workflowNextTaskType: {
                    columnName: 'workflow_next_task_type',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CaseType),
                    },
                },
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CaseStatus),
                    },
                },
                preventBilling: {
                    columnName: 'prevent_billing',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                responsibleReferenceId: {
                    columnName: 'responsible_reference_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                endReasonId: {
                    columnName: 'end_reason_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tags: {
                    columnName: 'tags',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                billingStartDate: {
                    columnName: 'billing_start_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                billingModelId: {
                    columnName: 'billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingAppendixSetupId: {
                    columnName: 'billing_appendix_setup_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                prediction: {
                    columnName: 'prediction',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                customerId: {
                    columnName: 'customer_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                workflowNextTaskDate: {
                    columnName: 'workflow_next_task_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                workflowNextTaskId: {
                    columnName: 'workflow_next_task_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                workflowCurrentTaskDate: {
                    columnName: 'workflow_current_task_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                workflowCurrentTaskId: {
                    columnName: 'workflow_current_task_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                workflowId: {
                    columnName: 'workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                statusLog: {
                    columnName: 'status_log',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                deleted: {
                    columnName: 'deleted',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                fromCaseId: {
                    columnName: 'from_case_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                isMigrated: {
                    columnName: 'is_migrated',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                alternativeContactId: {
                    columnName: 'alternative_contact_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                debtorContactId: {
                    columnName: 'debtor_contact_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                pauseUpdateId: {
                    columnName: 'pause_update_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                collectorId: {
                    columnName: 'collector_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                importId: {
                    columnName: 'import_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                isCompany: {
                    columnName: 'is_company',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                caseGroupId: {
                    columnName: 'case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                statusTime: {
                    columnName: 'status_time',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        collectors: {
            tableName: 'collectors',
            primaryKey: 'collector_id',
            scope: 'collectors',
            properties: {
                collectorId: {
                    columnName: 'collector_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                theme: {
                    columnName: 'theme',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                logoFileId: {
                    columnName: 'logo_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                emailServerId: {
                    columnName: 'email_server_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CollectorType),
                    },
                },
                centralRegisterId: {
                    columnName: 'central_register_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                nickname: {
                    columnName: 'nickname',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                title: {
                    columnName: 'title',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                website: {
                    columnName: 'website',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                clientWebBase: {
                    columnName: 'client_web_base',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                debtorWebBase: {
                    columnName: 'debtor_web_base',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                privacyPolicy: {
                    columnName: 'privacy_policy',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                businessTerms: {
                    columnName: 'business_terms',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                email: {
                    columnName: 'email',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                replyTo: {
                    columnName: 'reply_to',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                address: {
                    columnName: 'address',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                dnsVerified: {
                    columnName: 'dns_verified',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                dnsSetup: {
                    columnName: 'dns_setup',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                hideEmail: {
                    columnName: 'hide_email',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                eboksSetup: {
                    columnName: 'eboks_setup',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                letterFrontLogoFileId: {
                    columnName: 'letter_front_logo_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                paymentOptions: {
                    columnName: 'payment_options',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                phone: {
                    columnName: 'phone',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                emailTemplateHtml: {
                    columnName: 'email_template_html',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                emailTemplateBody: {
                    columnName: 'email_template_body',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                envelope: {
                    columnName: 'envelope',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
            },
        },
        connectCaseMappingUpdates: {
            tableName: 'connect_case_mapping_updates',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                createdByTenantId: {
                    columnName: 'created_by_tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(ConnectCaseMappingStatus),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                connectCaseMappingId: {
                    columnName: 'connect_case_mapping_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
            },
        },
        connectCaseMappings: {
            tableName: 'connect_case_mappings',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                originCaseId: {
                    columnName: 'origin_case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdCaseId: {
                    columnName: 'created_case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                connectConnectionId: {
                    columnName: 'connect_connection_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        connectConnectionUpdates: {
            tableName: 'connect_connection_updates',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                connectConnectionId: {
                    columnName: 'connect_connection_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                createdByTenantId: {
                    columnName: 'created_by_tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(ConnectConnectionStatus),
                    },
                },
            },
        },
        connectConnections: {
            tableName: 'connect_connections',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                fromTenantId: {
                    columnName: 'from_tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                toTenantId: {
                    columnName: 'to_tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                productId: {
                    columnName: 'product_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                toCreditorId: {
                    columnName: 'to_creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                fromCreditorId: {
                    columnName: 'from_creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                options: {
                    columnName: 'options',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        connectEntityMappings: {
            tableName: 'connect_entity_mappings',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(EntityType),
                    },
                },
                source: {
                    columnName: 'source',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                connectConnectionId: {
                    columnName: 'connect_connection_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdEntityId: {
                    columnName: 'created_entity_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                originEntityId: {
                    columnName: 'origin_entity_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                connectMappingId: {
                    columnName: 'connect_mapping_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        connectPartners: {
            tableName: 'connect_partners',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                logo: {
                    columnName: 'logo',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
            },
        },
        connectProducts: {
            tableName: 'connect_products',
            primaryKey: 'id',
            scope: 'connect',
            properties: {
                partnerId: {
                    columnName: 'partner_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                content: {
                    columnName: 'content',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                service: {
                    columnName: 'service',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                caseGroupId: {
                    columnName: 'case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                billingModelId: {
                    columnName: 'billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                workflowId: {
                    columnName: 'workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                logo: {
                    columnName: 'logo',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        cosmoEvents: {
            tableName: 'cosmo_events',
            primaryKey: 'cosmo_event_id',
            scope: 'cosmo',
            properties: {
                cosmoEventId: {
                    columnName: 'cosmo_event_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                source: {
                    columnName: 'source',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(SourceType),
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(EventType),
                    },
                },
                starId: {
                    columnName: 'star_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        creditors: {
            tableName: 'creditors',
            primaryKey: 'creditor_id',
            scope: 'creditors',
            properties: {
                identityCollectorId: {
                    columnName: 'identity_collector_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                logoFileId: {
                    columnName: 'logo_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                iconFileId: {
                    columnName: 'icon_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                defaultWorkflowId: {
                    columnName: 'default_workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                defaultCollectorId: {
                    columnName: 'default_collector_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                caseImportMap: {
                    columnName: 'case_import_map',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                defaultCaseGroupId: {
                    columnName: 'default_case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                aksEnabled: {
                    columnName: 'aks_enabled',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                defaultConnectorId: {
                    columnName: 'default_connector_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                priceList: {
                    columnName: 'price_list',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                vatRegistered: {
                    columnName: 'vat_registered',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                isCompany: {
                    columnName: 'is_company',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                acquisitionResponsibleUserId: {
                    columnName: 'acquisition_responsible_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                contactUserId: {
                    columnName: 'contact_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                caseworkerContactUserId: {
                    columnName: 'caseworker_contact_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                settings: {
                    columnName: 'settings',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                defaultCreditorWorkflowId: {
                    columnName: 'default_creditor_workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                selfCollection: {
                    columnName: 'self_collection',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                outboundCall: {
                    columnName: 'outbound_call',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                defaultDunningConnectorId: {
                    columnName: 'default_dunning_connector_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                defaultProcedures: {
                    columnName: 'default_procedures',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                caseGroupWorkflows: {
                    columnName: 'case_group_workflows',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                customVoucherApplications: {
                    columnName: 'custom_voucher_applications',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                bankAccountDetails: {
                    columnName: 'bank_account_details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                address: {
                    columnName: 'address',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                caseMergeRules: {
                    columnName: 'case_merge_rules',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                paymentPlansConfig: {
                    columnName: 'payment_plans_config',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                autoVerify: {
                    columnName: 'auto_verify',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                billingEmails: {
                    columnName: 'billing_emails',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                paymentTerms: {
                    columnName: 'payment_terms',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                vat: {
                    columnName: 'vat',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                defaultBillingModelId: {
                    columnName: 'default_billing_model_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                defaultBillingAppendixSetupId: {
                    columnName: 'default_billing_appendix_setup_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                minimumBillingLimit: {
                    columnName: 'minimum_billing_limit',
                    type: {
                        type: 'money',
                        nullable: true,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                notes: {
                    columnName: 'notes',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                experianClientId: {
                    columnName: 'experian_client_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                centralRegisterId: {
                    columnName: 'central_register_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CreditorState),
                    },
                },
                debtorFaq: {
                    columnName: 'debtor_faq',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                allowedIntegrations: {
                    columnName: 'allowed_integrations',
                    type: {
                        type: 'unknown',
                        nullable: false,
                    },
                },
                erpLink: {
                    columnName: 'erp_link',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
            },
        },
        customers: {
            tableName: 'customers',
            primaryKey: 'customer_id',
            scope: 'customers',
            properties: {
                defaultCaseGroupId: {
                    columnName: 'default_case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                alternativeContactMigrated: {
                    columnName: 'alternative_contact_migrated',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                tags: {
                    columnName: 'tags',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                bankAccount: {
                    columnName: 'bank_account',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                customerId: {
                    columnName: 'customer_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                isCompany: {
                    columnName: 'is_company',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
            },
        },
        documents: {
            tableName: 'documents',
            primaryKey: 'document_id',
            scope: 'documents',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                signedAt: {
                    columnName: 'signed_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                refId: {
                    columnName: 'ref_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                signedFileId: {
                    columnName: 'signed_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                fileId: {
                    columnName: 'file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                documentId: {
                    columnName: 'document_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                minAccessRole: {
                    columnName: 'min_access_role',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                refIdType: {
                    columnName: 'ref_id_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(RefIdTypes),
                    },
                },
                date: {
                    columnName: 'date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                caseFileId: {
                    columnName: 'case_file_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                meta: {
                    columnName: 'meta',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(DocumentTypes),
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
            },
        },
        emailServers: {
            tableName: 'email_servers',
            primaryKey: 'id',
            scope: 'email-servers',
            properties: {
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                from: {
                    columnName: 'from',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                email: {
                    columnName: 'email',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                provider: {
                    columnName: 'provider',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        endReasonProxies: {
            tableName: 'end_reason_proxies',
            primaryKey: '',
            scope: 'end-reasons',
            properties: {
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                endReasonId: {
                    columnName: 'end_reason_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        endReasonVisibility: {
            tableName: 'end_reason_visibility',
            primaryKey: '',
            scope: 'end-reasons',
            properties: {
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                endReasonId: {
                    columnName: 'end_reason_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        endReasons: {
            tableName: 'end_reasons',
            primaryKey: 'id',
            scope: 'end-reasons',
            properties: {
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        files: {
            tableName: 'files',
            primaryKey: 'id',
            scope: 'files',
            properties: {
                bytes: {
                    columnName: 'bytes',
                    type: {
                        type: 'int',
                        nullable: true,
                    },
                },
                fileName: {
                    columnName: 'file_name',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                folder: {
                    columnName: 'folder',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                fileSeqId: {
                    columnName: 'file_seq_id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdByAccountId: {
                    columnName: 'created_by_account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                contentType: {
                    columnName: 'content_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(FileType),
                    },
                },
                meta: {
                    columnName: 'meta',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        importFiles: {
            tableName: 'import_files',
            primaryKey: 'import_file_id',
            scope: 'imports',
            properties: {
                importFileId: {
                    columnName: 'import_file_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                fileId: {
                    columnName: 'file_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                importId: {
                    columnName: 'import_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        imports: {
            tableName: 'imports',
            primaryKey: 'import_id',
            scope: 'imports',
            properties: {
                importId: {
                    columnName: 'import_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByAccountId: {
                    columnName: 'created_by_account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                source: {
                    columnName: 'source',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: ['WEBAPP', 'API'],
                    },
                },
                caseType: {
                    columnName: 'case_type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(CaseType),
                    },
                },
                caseImportMap: {
                    columnName: 'case_import_map',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                rawImportData: {
                    columnName: 'raw_import_data',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(ImportState),
                    },
                },
                allFinallySettled: {
                    columnName: 'all_finally_settled',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                caseGroupId: {
                    columnName: 'case_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                allTerminated: {
                    columnName: 'all_terminated',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                importFileId: {
                    columnName: 'import_file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                importResult: {
                    columnName: 'import_result',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                migration: {
                    columnName: 'migration',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                importedAt: {
                    columnName: 'imported_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        importsCore: {
            tableName: 'imports_core',
            primaryKey: 'id',
            scope: 'imports',
            properties: {
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                completedJobCount: {
                    columnName: 'completed_job_count',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdJobCount: {
                    columnName: 'created_job_count',
                    type: {
                        type: 'int',
                        nullable: true,
                    },
                },
            },
        },
        importsCoreErrors: {
            tableName: 'imports_core_errors',
            primaryKey: 'id',
            scope: 'imports',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                importId: {
                    columnName: 'import_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                locations: {
                    columnName: 'locations',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                error: {
                    columnName: 'error',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        interactionLookup: {
            tableName: 'interaction_lookup',
            primaryKey: 'interaction_id',
            scope: 'interactions',
            properties: {
                key: {
                    columnName: 'key',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                interactionId: {
                    columnName: 'interaction_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(InteractionType),
                    },
                },
            },
        },
        interactions: {
            tableName: 'interactions',
            primaryKey: 'interaction_id',
            scope: 'interactions',
            properties: {
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(InteractionType),
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(IncomingState).concat(Object.values(OutgoingState)),
                    },
                },
                direction: {
                    columnName: 'direction',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(Direction),
                    },
                },
                queuedStepId: {
                    columnName: 'queued_step_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                casesImportId: {
                    columnName: 'cases_import_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                recipientId: {
                    columnName: 'recipient_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                referenceType: {
                    columnName: 'reference_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(Entity),
                    },
                },
                templateId: {
                    columnName: 'template_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                minVisibilityRole: {
                    columnName: 'min_visibility_role',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                seqId: {
                    columnName: 'seq_id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                intent: {
                    columnName: 'intent',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                templateVersionId: {
                    columnName: 'template_version_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                events: {
                    columnName: 'events',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                attachments: {
                    columnName: 'attachments',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                handlerId: {
                    columnName: 'handler_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                notes: {
                    columnName: 'notes',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                debtorId: {
                    columnName: 'debtor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                transacted: {
                    columnName: 'transacted',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        parkingLotRemarks: {
            tableName: 'parking_lot_remarks',
            primaryKey: 'parking_lot_remark_id',
            scope: 'parking',
            properties: {
                validTo: {
                    columnName: 'valid_to',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                parkingLotRemarkId: {
                    columnName: 'parking_lot_remark_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                comment: {
                    columnName: 'comment',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                parkingLotReferenceId: {
                    columnName: 'parking_lot_reference_id',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        paymentIdentifications: {
            tableName: 'payment_identifications',
            primaryKey: 'id',
            scope: 'cases',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                paymentIdentificationId: {
                    columnName: 'payment_identification_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        paymentMethods: {
            tableName: 'payment_methods',
            primaryKey: '',
            scope: 'payments',
            properties: {
                deletedAt: {
                    columnName: 'deleted_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                savedAt: {
                    columnName: 'saved_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                provider: {
                    columnName: 'provider',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Provider),
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PaymentMethodStatus),
                    },
                },
                collectorId: {
                    columnName: 'collector_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                paymentMethodId: {
                    columnName: 'payment_method_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                acceptUrl: {
                    columnName: 'accept_url',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                cancelUrl: {
                    columnName: 'cancel_url',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                paymentOptionId: {
                    columnName: 'payment_option_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
            },
        },
        paymentPlanEntries: {
            tableName: 'payment_plan_entries',
            primaryKey: 'paymentPlan_entry_id',
            scope: 'payment-plans',
            properties: {
                date: {
                    columnName: 'date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                processing: {
                    columnName: 'processing',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                amount: {
                    columnName: 'amount',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                cancel: {
                    columnName: 'cancel',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                deadline: {
                    columnName: 'deadline',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                reminder: {
                    columnName: 'reminder',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                done: {
                    columnName: 'done',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PaymentPlanEntryStatus),
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PaymentPlanEntryTypes),
                    },
                },
                paymentPlanId: {
                    columnName: 'payment_plan_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                paymentPlanEntryId: {
                    columnName: 'payment_plan_entry_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        paymentPlans: {
            tableName: 'payment_plans',
            primaryKey: 'payment_plan_id',
            scope: 'payment-plans',
            properties: {
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                paymentPlanId: {
                    columnName: 'payment_plan_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                deletedByUserId: {
                    columnName: 'deleted_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                oneDayActivationReminder: {
                    columnName: 'one_day_activation_reminder',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PaymentPlanStatus),
                    },
                },
                config: {
                    columnName: 'config',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                deletedAt: {
                    columnName: 'deleted_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                paymentMethodId: {
                    columnName: 'payment_method_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                threeDayActivationReminder: {
                    columnName: 'three_day_activation_reminder',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                plan: {
                    columnName: 'plan',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                documentId: {
                    columnName: 'document_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                twoHourActivationReminder: {
                    columnName: 'two_hour_activation_reminder',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                activationTrigger: {
                    columnName: 'activation_trigger',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        payouts: {
            tableName: 'payouts',
            primaryKey: 'id',
            scope: 'payouts',
            properties: {
                updatedBy: {
                    columnName: 'updated_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                updatedAt: {
                    columnName: 'updated_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PayoutState),
                    },
                },
                currency: {
                    columnName: 'currency',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Currency),
                    },
                },
                recipientText: {
                    columnName: 'recipient_text',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                referenceType: {
                    columnName: 'reference_type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(Entity),
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PayoutType),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                amount: {
                    columnName: 'amount',
                    type: {
                        type: 'money',
                        nullable: false,
                    },
                },
                account: {
                    columnName: 'account',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        procedures: {
            tableName: 'procedures',
            primaryKey: 'procedure_id',
            scope: 'procedures',
            properties: {
                title: {
                    columnName: 'title',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                collectorId: {
                    columnName: 'collector_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                showOnCase: {
                    columnName: 'show_on_case',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                tasks: {
                    columnName: 'tasks',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                procedureId: {
                    columnName: 'procedure_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        proceduresOnTenants: {
            tableName: 'procedures_on_tenants',
            primaryKey: '',
            scope: 'procedures',
            properties: {
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                procedureId: {
                    columnName: 'procedure_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        properties: {
            tableName: 'properties',
            primaryKey: 'property_id',
            scope: 'properties',
            properties: {
                refId: {
                    columnName: 'ref_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                propertyId: {
                    columnName: 'property_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PropertyDataSchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                current: {
                    columnName: 'current',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PropertyDataType),
                    },
                },
                ref: {
                    columnName: 'ref',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: [Entity.Case, Entity.CaseVoucher, Entity.Creditor],
                    },
                },
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        propertyTypes: {
            tableName: 'property_types',
            primaryKey: 'property_type_id',
            scope: 'properties',
            properties: {
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                handle: {
                    columnName: 'handle',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                propertyTypeId: {
                    columnName: 'property_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                required: {
                    columnName: 'required',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                scopeId: {
                    columnName: 'scope_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                hidden: {
                    columnName: 'hidden',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                searchable: {
                    columnName: 'searchable',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                ref: {
                    columnName: 'ref',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: [Entity.Case, Entity.CaseVoucher, Entity.Creditor],
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(PropertyDataType),
                    },
                },
                scope: {
                    columnName: 'scope',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: ['case_groups'],
                    },
                },
                permissionLevelEdit: {
                    columnName: 'permission_level_edit',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: roles,
                    },
                },
                permissionLevel: {
                    columnName: 'permission_level',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: roles,
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        satisfactions: {
            tableName: 'satisfactions',
            primaryKey: 'satisfaction_id',
            scope: 'satisfactions',
            properties: {
                satisfaction: {
                    columnName: 'satisfaction',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                accountId: {
                    columnName: 'account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                refId: {
                    columnName: 'ref_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                satisfactionId: {
                    columnName: 'satisfaction_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                comment: {
                    columnName: 'comment',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                satisfiedAt: {
                    columnName: 'satisfied_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        templates: {
            tableName: 'templates',
            primaryKey: 'template_id',
            scope: 'templates',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                hidden: {
                    columnName: 'hidden',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                tags: {
                    columnName: 'tags',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                isDefaultLang: {
                    columnName: 'is_default_lang',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                intent: {
                    columnName: 'intent',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                isForAlternativeContact: {
                    columnName: 'is_for_alternative_contact',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                versionId: {
                    columnName: 'version_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdByAccountId: {
                    columnName: 'created_by_account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                collectorId: {
                    columnName: 'collector_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                forManualUse: {
                    columnName: 'for_manual_use',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                setup: {
                    columnName: 'setup',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                templateId: {
                    columnName: 'template_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                templateTheme: {
                    columnName: 'template_theme',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                folder: {
                    columnName: 'folder',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TemplateType),
                    },
                },
                html: {
                    columnName: 'html',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                text: {
                    columnName: 'text',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                subject: {
                    columnName: 'subject',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TemplateState),
                    },
                },
                fileName: {
                    columnName: 'file_name',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                lang: {
                    columnName: 'lang',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        templatesOnTenants: {
            tableName: 'templates_on_tenants',
            primaryKey: 'id',
            scope: 'templates',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                templateId: {
                    columnName: 'template_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        transactionAccounts: {
            tableName: 'transaction_accounts',
            primaryKey: 'transaction_account_id',
            scope: 'transaction-accounts',
            properties: {
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TransactionAccountType),
                    },
                },
                transactionAccountId: {
                    columnName: 'transaction_account_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                name: {
                    columnName: 'name',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
            },
        },
        transactionImports: {
            tableName: 'transaction_imports',
            primaryKey: 'id',
            scope: 'transaction-accounts',
            properties: {
                transactionAccountId: {
                    columnName: 'transaction_account_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                fileId: {
                    columnName: 'file_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                result: {
                    columnName: 'result',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        tribunalCases: {
            tableName: 'tribunal_cases',
            primaryKey: 'id',
            scope: 'tribunal',
            properties: {
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TribunalCaseType),
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                status: {
                    columnName: 'status',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TribunalCaseStatus),
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                reference: {
                    columnName: 'reference',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
            },
        },
        tribunalJobs: {
            tableName: 'tribunal_jobs',
            primaryKey: 'id',
            scope: 'tribunal',
            properties: {
                tribunalCaseId: {
                    columnName: 'tribunal_case_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                attempts: {
                    columnName: 'attempts',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                result: {
                    columnName: 'result',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                completedAt: {
                    columnName: 'completed_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                type: {
                    columnName: 'type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(TribunalJobType),
                    },
                },
                reference: {
                    columnName: 'reference',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                caseId: {
                    columnName: 'case_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
            },
        },
        updateTypeGroups: {
            tableName: 'update_type_groups',
            primaryKey: 'update_type_group_id',
            scope: 'updates',
            properties: {
                updateTypeGroupId: {
                    columnName: 'update_type_group_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                short: {
                    columnName: 'short',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        updateTypes: {
            tableName: 'update_types',
            primaryKey: 'update_type_id',
            scope: 'updates',
            properties: {
                responsiblePermissionLevel: {
                    columnName: 'responsible_permission_level',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: roles,
                    },
                },
                continueOnCaseExit: {
                    columnName: 'continue_on_case_exit',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                updateTypeId: {
                    columnName: 'update_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                hiddenPermissionLevel: {
                    columnName: 'hidden_permission_level',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: roles,
                    },
                },
                pauseWithUpdateOnCaseExit: {
                    columnName: 'pause_with_update_on_case_exit',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                rottensAfterMs: {
                    columnName: 'rottens_after_ms',
                    type: {
                        type: 'int',
                        nullable: true,
                    },
                },
                priority: {
                    columnName: 'priority',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                importantTheme: {
                    columnName: 'important_theme',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                procedureEnabled: {
                    columnName: 'procedure_enabled',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                procedure: {
                    columnName: 'procedure',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                updateTypeGroupId: {
                    columnName: 'update_type_group_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                selectable: {
                    columnName: 'selectable',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                referenceType: {
                    columnName: 'reference_type',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(UpdateReferenceTypes),
                    },
                },
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                knowledgeBaseUrls: {
                    columnName: 'knowledge_base_urls',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                pausing: {
                    columnName: 'pausing',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                important: {
                    columnName: 'important',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                introductionHtml: {
                    columnName: 'introduction_html',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                introductionHeroFile: {
                    columnName: 'introduction_hero_file',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
            },
        },
        updateTypesOnTenants: {
            tableName: 'update_types_on_tenants',
            primaryKey: '',
            scope: 'updates',
            properties: {
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                updateTypeId: {
                    columnName: 'update_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        updates: {
            tableName: 'updates',
            primaryKey: 'update_id',
            scope: 'updates',
            properties: {
                migratedId: {
                    columnName: 'migrated_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                intention: {
                    columnName: 'intention',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                update: {
                    columnName: 'update',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                taskId: {
                    columnName: 'task_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                responsibleType: {
                    columnName: 'responsible_type',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: ['user', 'team'],
                    },
                },
                state: {
                    columnName: 'state',
                    type: {
                        type: 'enum',
                        nullable: true,
                        values: Object.values(UpdateState),
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                continuedBy: {
                    columnName: 'continued_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                responsibleManuallySet: {
                    columnName: 'responsible_manually_set',
                    type: {
                        type: 'boolean',
                        nullable: true,
                    },
                },
                byWorkflowId: {
                    columnName: 'by_workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                responsibleReferenceId: {
                    columnName: 'responsible_reference_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                procedure: {
                    columnName: 'procedure',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                continuedAt: {
                    columnName: 'continued_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                remindedAt: {
                    columnName: 'reminded_at',
                    type: {
                        type: 'date',
                        nullable: false,
                    },
                },
                pausedAt: {
                    columnName: 'paused_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                updateTypeId: {
                    columnName: 'update_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                data: {
                    columnName: 'data',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                refId: {
                    columnName: 'ref_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                updateId: {
                    columnName: 'update_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        urlShorteners: {
            tableName: 'url_shorteners',
            primaryKey: 'url_shortener_id',
            scope: 'url-shorteners',
            properties: {
                referenceType: {
                    columnName: 'reference_type',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                expirationDate: {
                    columnName: 'expiration_date',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                source: {
                    columnName: 'source',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                urlShortenerId: {
                    columnName: 'url_shortener_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                url: {
                    columnName: 'url',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
            },
        },
        usersOnCustomers: {
            tableName: 'users_on_customers',
            primaryKey: 'user_on_customer_id',
            scope: 'customers',
            properties: {
                referenceId: {
                    columnName: 'reference_id',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                lang: {
                    columnName: 'lang',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                userOnCustomerId: {
                    columnName: 'user_on_customer_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                oldDebtorId: {
                    columnName: 'old_debtor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                userId: {
                    columnName: 'user_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                contact: {
                    columnName: 'contact',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                relation: {
                    columnName: 'relation',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: ['debtor', 'alternative-contact'],
                    },
                },
                details: {
                    columnName: 'details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: UserOnCustomerDetailsSchema,
                    },
                },
                name: {
                    columnName: `details->>'name'`,
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                creationDetails: {
                    columnName: 'creation_details',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                customerId: {
                    columnName: 'customer_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                subscribedToCosmo: {
                    columnName: 'subscribed_to_cosmo',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        voucherApplications: {
            tableName: 'voucher_applications',
            primaryKey: 'voucher_application_id',
            scope: 'voucher-applications',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                enabled: {
                    columnName: 'enabled',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                application: {
                    columnName: 'application',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                voucherApplicationId: {
                    columnName: 'voucher_application_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                creditorId: {
                    columnName: 'creditor_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
            },
        },
        voucherTypes: {
            tableName: 'voucher_types',
            primaryKey: 'voucher_type_id',
            scope: 'economy.vouchers',
            properties: {
                order: {
                    columnName: 'order',
                    type: {
                        type: 'int',
                        nullable: false,
                    },
                },
                settingDetails: {
                    columnName: 'setting_details',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                setting: {
                    columnName: 'setting',
                    type: {
                        type: 'string',
                        nullable: true,
                    },
                },
                vat: {
                    columnName: 'vat',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                category: {
                    columnName: 'category',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(VoucherCategory),
                    },
                },
                label: {
                    columnName: 'label',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                voucherTypeId: {
                    columnName: 'voucher_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        voucherTypesOnTenants: {
            tableName: 'voucher_types_on_tenants',
            primaryKey: 'id',
            scope: 'economy.vouchers',
            properties: {
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                voucherTypeId: {
                    columnName: 'voucher_type_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
            },
        },
        workflows: {
            tableName: 'workflows',
            primaryKey: 'workflow_id',
            scope: 'workflows',
            properties: {
                createdByUserId: {
                    columnName: 'created_by_user_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                workflowId: {
                    columnName: 'workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tasks: {
                    columnName: 'tasks',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                paths: {
                    columnName: 'paths',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: z.unknown(),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
                clonedFromWorkflowId: {
                    columnName: 'cloned_from_workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: true,
                    },
                },
                title: {
                    columnName: 'title',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                description: {
                    columnName: 'description',
                    type: {
                        type: 'string',
                        nullable: false,
                    },
                },
                public: {
                    columnName: 'public',
                    type: {
                        type: 'boolean',
                        nullable: false,
                    },
                },
                stages: {
                    columnName: 'stages',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: z.unknown(),
                    },
                },
            },
        },
        workflowsOnTenants: {
            tableName: 'workflows_on_tenants',
            primaryKey: 'id',
            scope: 'workflows',
            properties: {
                createdBy: {
                    columnName: 'created_by',
                    type: {
                        type: 'json',
                        nullable: true,
                        schema: PerformedBySchema,
                    },
                },
                workflowId: {
                    columnName: 'workflow_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                id: {
                    columnName: 'id',
                    type: {
                        type: 'uuid',
                        nullable: false,
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: {
                        type: 'date',
                        nullable: true,
                    },
                },
            },
        },
        views: {
            tableName: 'views',
            primaryKey: 'id',
            scope: 'views',
            properties: {
                id: {
                    columnName: 'id',
                    type: { type: 'uuid', nullable: false },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: { type: 'uuid', nullable: false },
                },
                name: {
                    columnName: 'name',
                    type: { type: 'string', nullable: false },
                },
                options: {
                    columnName: 'options',
                    type: {
                        type: 'json',
                        nullable: false,
                        schema: ViewDescriptionSchema,
                    },
                },
                visibility: {
                    columnName: 'visibility',
                    type: {
                        type: 'enum',
                        nullable: false,
                        values: Object.values(ViewVisibilityType),
                    },
                },
                createdAt: {
                    columnName: 'created_at',
                    type: { type: 'date', nullable: false },
                },
                updatedAt: {
                    columnName: 'updated_at',
                    type: { type: 'date', nullable: false },
                },
                createdBy: {
                    columnName: 'created_by',
                    type: { type: 'json', nullable: false, schema: PerformedBySchema },
                },
                createdById: {
                    columnName: `created_by ->> 'id'`,
                    type: { type: 'string', nullable: false },
                },
                updatedBy: {
                    columnName: 'updated_by',
                    type: { type: 'json', nullable: false, schema: PerformedBySchema },
                },
            },
        },
        favoritedViews: {
            tableName: 'favorited_views',
            primaryKey: 'id',
            scope: 'views',
            properties: {
                id: {
                    columnName: 'id',
                    type: { type: 'uuid', nullable: false },
                },
                tenantId: {
                    columnName: 'tenant_id',
                    type: { type: 'uuid', nullable: false },
                },
                userId: {
                    columnName: 'user_id',
                    type: { type: 'uuid', nullable: false },
                },
                viewId: {
                    columnName: 'view_id',
                    type: { type: 'uuid', nullable: false },
                },
            },
        },
    },
    relations: {
        creditors: {
            cases: {
                fields: [
                    {
                        fromKey: 'creditorId',
                        toKey: 'creditorId',
                    },
                ],
            },
        },
        customers: {
            cases: {
                fields: [
                    {
                        fromKey: 'customerId',
                        toKey: 'customerId',
                    },
                ],
            },
            usersOnCustomers: {
                fields: [
                    {
                        fromKey: 'customerId',
                        toKey: 'customerId',
                    },
                ],
            },
        },
        cases: {
            interactions: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'referenceId',
                    },
                ],
            },
            caseVouchers: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'caseId',
                    },
                ],
            },
            updates: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'referenceId',
                    },
                ],
            },
            documents: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'refId',
                    },
                ],
            },
            satisfactions: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'refId',
                    },
                ],
            },
            properties: {
                fields: [
                    {
                        fromKey: 'caseId',
                        toKey: 'refId',
                    },
                ],
            },
        },
        caseVouchers: {
            properties: {
                fields: [
                    {
                        fromKey: 'caseVoucherId',
                        toKey: 'refId',
                    },
                ],
            },
        },
        billingModels: {
            cases: {
                fields: [
                    {
                        fromKey: 'billingModelId',
                        toKey: 'billingModelId',
                    },
                ],
            },
        },
        caseGroups: {
            cases: {
                fields: [
                    {
                        fromKey: 'caseGroupId',
                        toKey: 'caseGroupId',
                    },
                ],
            },
        },
        voucherTypes: {
            caseVouchers: {
                fields: [
                    {
                        fromKey: 'voucherTypeId',
                        toKey: 'voucherTypeId',
                    },
                ],
            },
        },
        workflows: {
            cases: {
                fields: [
                    {
                        fromKey: 'workflowId',
                        toKey: 'workflowId',
                    },
                ],
            },
        },
        updateTypes: {
            updates: {
                fields: [
                    {
                        fromKey: 'updateTypeId',
                        toKey: 'updateTypeId',
                    },
                ],
            },
        },
        propertyTypes: {
            properties: {
                fields: [
                    {
                        fromKey: 'tenantId',
                        toKey: 'tenantId',
                    },
                    {
                        fromKey: 'handle',
                        toKey: 'handle',
                    },
                ],
            },
        },
        endReasons: {
            cases: {
                fields: [{ fromKey: 'id', toKey: 'endReasonId' }],
            },
        },
        favoritedViews: {
            views: {
                fields: [{ fromKey: 'viewId', toKey: 'id' }],
            },
        },
    },
});
//# sourceMappingURL=db.js.map